/** @internal */
export class Result<T, E = Error> {
    public static Ok<T>(value: T): Result.Ok<T> {
        return new Result(value)
    }

    public static Err<E>(error: E): Result.Err<E> {
        return new Result(undefined, error)
    }

    private readonly ok: T | null | undefined = undefined
    private readonly err: E | null = null

    private constructor(ok: T)
    private constructor(ok: null | undefined, err: E)
    private constructor(ok: T | null | undefined, err: E | null = null) {
        if (err !== null) {
            this.err = err as E
        } else if (ok !== undefined) {
            this.ok = ok
        }
    }

    unwrap(): T {
        if (this.isOk()) {
            return this.ok as T
        }

        if (this.isErr()) {
            throw this.err as E
        }

        throw new Error('Unknown error')
    }

    unwrapOr(defaultValue: T): T {
        return this.ok ?? defaultValue
    }

    unwrapOrNull(): T | null {
        return this.ok ?? null
    }

    isOk(): this is Result.Ok<T> {
        return this.err === null && this.ok !== undefined
    }

    isErr(): this is Result.Err<E> {
        return this.err !== null
    }

    getErr(): this extends Result.Err<E> ? E : null {
        return this.err as this extends Result.Err<E> ? E : null
    }

    toString = (): string => {
        if (this.isErr()) {
            const err = this.getErr()
            return `Result.Err: ${err instanceof Error ? err.message : err}`
        }

        return `Result.Ok: ${this.unwrapOrNull()}`
    }
}

/**
 * Adds type extensions allowing:
 * ```typescript
 * function parseResultError(err: Result.Err) {}
 * ```
 *
 * @internal
 * */
export module Result {
    export type Err<E = Error> = Result<never, E>
    export type Ok<T = any> = Result<T, never>
}
